<template>
  <div class="event-order-detail" v-loading="loading">
    <el-descriptions :column="2" border>
      <el-descriptions-item label="名称">{{ order.name }}</el-descriptions-item>
      <el-descriptions-item label="订单号">{{ order.number }}</el-descriptions-item>
      <el-descriptions-item label="状态"><el-tag :type="statusType">{{ statusLabel }}</el-tag></el-descriptions-item>
      <el-descriptions-item label="活动类型">{{ order.templateName }}</el-descriptions-item>
      <el-descriptions-item label="客户名称">{{ order.contact || order.contract }}</el-descriptions-item>
      <el-descriptions-item label="联系电话">{{ order.contactPhone || order.contractPhone }}</el-descriptions-item>
      <el-descriptions-item label="活动方">{{ order.host }}</el-descriptions-item>
      <el-descriptions-item label="活动时间">{{ order.eventTime }}</el-descriptions-item>
    </el-descriptions>

    <el-tabs v-model="activeTab">
      <el-tab-pane label="执行流程" name="flow">
        <div v-if="!order.groups || !order.groups.length" class="empty">该订单没有执行流程</div>
        <el-card v-for="(group, groupIndex) in order.groups" :key="group.id" shadow="never" class="group-card">
          <div slot="header" class="group-title">
            <strong>{{ groupIndex + 1 }}. {{ group.name }}</strong>
            <span><el-tag v-if="group.isRandom" size="mini">乱序</el-tag><el-tag v-if="group.imageRequired" size="mini" type="warning">图片必填</el-tag></span>
          </div>
          <div v-for="(step, stepIndex) in group.steps" :key="step.id" class="step-row">
            <div>
              <i :class="step.checked ? 'el-icon-success checked' : 'el-icon-time'"/>
              <span>{{ stepIndex + 1 }}. {{ step.name }}</span>
              <span v-if="step.images && step.images.length" class="image-count">{{ step.images.length }} 张图片</span>
            </div>
            <div v-if="order.status === 'UNFINISHED'">
              <el-button v-if="!step.checked" type="primary" plain size="mini" @click="checkStep(step)">标记完成</el-button>
              <el-button v-else type="text" size="mini" @click="resetStep(step)">撤销完成</el-button>
            </div>
          </div>
        </el-card>
        <div class="finish-bar" v-if="order.status === 'UNFINISHED'">
          <el-button type="success" :loading="finishing" @click="finishOrder">完成整个订单</el-button>
        </div>
      </el-tab-pane>

      <el-tab-pane label="二维码" name="qrcode">
        <div class="qr-toolbar">
          <span>当前仅使用黄码和绿码。</span>
          <el-button size="mini" type="primary" plain :loading="refreshingQr" @click="regenerateQRCodes">重新生成二维码</el-button>
        </div>
        <div class="qr-grid">
          <div v-for="item in qrCodes" :key="item.label" class="qr-item">
            <div class="qr-title">{{ item.label }}</div>
            <div v-if="isLocalQr(item.url)" class="local-qr-placeholder">
              <i class="el-icon-picture-outline"/>
              <strong>本地模式二维码</strong>
              <span>{{ localQrScene(item.url) }}</span>
            </div>
            <el-image v-else-if="item.url" :src="item.url" :preview-src-list="[item.url]" fit="contain"/>
            <div v-else class="empty">尚未生成</div>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane label="反馈问题" name="feedback">
        <el-table :data="order.feedbacks || []" border>
          <el-table-column prop="question" label="问题"/>
          <el-table-column prop="type" label="类型" width="100"/>
          <el-table-column prop="value" label="回答"/>
        </el-table>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import {eventorder} from "@/api/eventorder";

export default {
  name: "EventOrderDetail",
  props: {id: Number},
  data() { return {order: {}, activeTab: "flow", loading: false, finishing: false, refreshingQr: false}; },
  computed: {
    statusLabel() { return {FINISHED: "已完成", UNFINISHED: "未完成", CANCELED: "已取消"}[this.order.status] || "未知"; },
    statusType() { return {FINISHED: "success", UNFINISHED: "warning", CANCELED: "danger"}[this.order.status] || "info"; },
    qrCodes() {
      return [{label: "黄码（客户/园长）", url: this.order.qrcode}, {label: "绿码（执行人员）", url: this.order.qrcodeB}];
    },
  },
  created() { this.load(); },
  methods: {
    isLocalQr(url) { return typeof url === "string" && url.startsWith("local://"); },
    localQrScene(url) { return url ? url.replace("local://wellorg/", "") : ""; },
    load() {
      this.loading = true;
      eventorder.getOrderDetail(this.id).then(res => { this.order = res.data || {}; })
          .finally(() => { this.loading = false; });
    },
    checkStep(step) {
      eventorder.checkStep(step.id).then(() => { this.$message.success("步骤已完成"); this.load(); });
    },
    resetStep(step) {
      this.$confirm("撤销后会同时删除该步骤已上传的图片，确定继续吗？", "提示", {type: "warning"})
          .then(() => eventorder.resetStep(step.id)).then(() => { this.$message.success("已撤销"); this.load(); }).catch(() => {});
    },
    finishOrder() {
      this.$confirm("确认所有流程都已执行完毕，并完成该订单吗？", "提示", {type: "warning"})
          .then(() => {
            this.finishing = true;
            return eventorder.finishOrder(this.id);
          }).then(() => { this.$message.success("订单已完成"); this.load(); this.$emit("updated"); })
          .catch(() => {}).finally(() => { this.finishing = false; });
    },
    regenerateQRCodes() {
      this.$confirm("将重新生成该订单的黄码和绿码，原二维码图片将被替换。是否继续？", "提示", {type: "warning"})
          .then(() => {
            this.refreshingQr = true;
            return eventorder.regenerateQRCodes(this.id);
          }).then(() => {
            this.$message.success("黄码和绿码已重新生成");
            this.load();
          }).catch(() => {}).finally(() => { this.refreshingQr = false; });
    },
  },
};
</script>

<style scoped>
.event-order-detail { padding: 4px; }
.el-tabs { margin-top: 18px; }
.group-card { margin-bottom: 12px; }
.group-title, .step-row { display: flex; align-items: center; justify-content: space-between; }
.group-title span { display: flex; gap: 6px; }
.step-row { min-height: 44px; border-bottom: 1px solid #ebeef5; }
.step-row:last-child { border-bottom: 0; }
.step-row i { margin-right: 8px; color: #909399; }
.step-row i.checked { color: #67c23a; }
.image-count { margin-left: 12px; color: #909399; font-size: 12px; }
.finish-bar { text-align: right; margin-top: 16px; }
.qr-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; color: #606266; }
.qr-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.qr-item { border: 1px solid #ebeef5; padding: 14px; text-align: center; }
.qr-title { font-weight: 600; margin-bottom: 10px; }
.qr-item .el-image { width: 180px; height: 180px; }
.local-qr-placeholder { width: 180px; height: 180px; margin: 0 auto; border: 1px dashed #c0c4cc; background: #f5f7fa; color: #606266; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; }
.local-qr-placeholder i { font-size: 34px; color: #909399; }
.local-qr-placeholder span { max-width: 150px; color: #909399; font-size: 12px; overflow-wrap: anywhere; }
.empty { padding: 30px; text-align: center; color: #909399; }
</style>
